/**
 * BlindConfigModal
 *
 * Modal for configuring blind structure before starting Texas Hold'em
 */

import { BlindConfiguration, BlindMode, BlindIncreaseFrequency } from '../types/holdem.types';

export class BlindConfigModal {
    private modal: HTMLDivElement | null = null;
    private onConfirm: ((config: BlindConfiguration) => void) | null = null;
    private currentMode: BlindMode = 'default';
    private config: BlindConfiguration;

    constructor() {
        // Default configuration
        this.config = {
            mode: 'default',
            initialSmallBlind: 10,
            initialBigBlind: 20,
            increaseFrequency: 'elimination',
            increaseMultiplier: 2
        };
    }

    public show(onConfirm: (config: BlindConfiguration) => void): void {
        this.onConfirm = onConfirm;
        this.createModal();
    }

    private createModal(): void {
        // Remove existing modal if any
        if (this.modal) {
            this.modal.remove();
        }

        // Create modal overlay
        this.modal = document.createElement('div');
        this.modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-family: 'Courier New', monospace;
        `;

        // Create modal content
        const content = document.createElement('div');
        content.style.cssText = `
            background: linear-gradient(180deg, #1a0033 0%, #2d0052 100%);
            border: 3px solid #FFD700;
            border-radius: 10px;
            padding: 30px;
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
        `;

        content.innerHTML = `
            <h2 style="color: #FFD700; margin: 0 0 20px 0; text-align: center; font-size: 28px; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);">
                🎰 Blind Configuration
            </h2>

            <div style="margin-bottom: 20px;">
                <label style="color: #FFDD00; font-size: 16px; display: block; margin-bottom: 10px; font-weight: bold;">
                    Choose Blind Mode:
                </label>
                <div id="mode-buttons" style="display: flex; gap: 10px; margin-bottom: 20px;">
                    <button id="mode-default" class="mode-btn mode-btn-active" data-mode="default" style="flex: 1; padding: 12px; background: #4CAF50; color: white; border: 2px solid #FFD700; border-radius: 5px; cursor: pointer; font-size: 14px; font-weight: bold;">
                        Default
                    </button>
                    <button id="mode-fixed" class="mode-btn" data-mode="fixed" style="flex: 1; padding: 12px; background: #333; color: white; border: 2px solid #666; border-radius: 5px; cursor: pointer; font-size: 14px; font-weight: bold;">
                        Fixed
                    </button>
                    <button id="mode-custom" class="mode-btn" data-mode="custom" style="flex: 1; padding: 12px; background: #333; color: white; border: 2px solid #666; border-radius: 5px; cursor: pointer; font-size: 14px; font-weight: bold;">
                        Custom
                    </button>
                </div>
            </div>

            <div id="mode-description" style="background: rgba(0, 0, 0, 0.3); padding: 15px; border-radius: 5px; margin-bottom: 20px; border: 1px solid #FFD700;">
                <p style="color: #FFDD00; margin: 0; font-size: 14px; line-height: 1.6;" id="description-text">
                    <strong>Default Mode:</strong> Blinds are calculated based on your starting balance. Blinds double when players are eliminated.
                </p>
            </div>

            <div id="config-panel">
                <!-- Dynamic content will be inserted here -->
            </div>

            <div style="display: flex; gap: 10px; margin-top: 30px;">
                <button id="cancel-btn" style="flex: 1; padding: 15px; background: #666; color: white; border: 2px solid #999; border-radius: 5px; cursor: pointer; font-size: 16px; font-weight: bold;">
                    Cancel
                </button>
                <button id="confirm-btn" style="flex: 1; padding: 15px; background: #4CAF50; color: white; border: 2px solid #FFD700; border-radius: 5px; cursor: pointer; font-size: 16px; font-weight: bold;">
                    Start Game
                </button>
            </div>
        `;

        this.modal.appendChild(content);
        document.body.appendChild(this.modal);

        // Attach event listeners
        this.attachEventListeners();

        // Initialize with default mode
        this.updateConfigPanel();
    }

    private attachEventListeners(): void {
        if (!this.modal) return;

        // Mode buttons
        const modeButtons = this.modal.querySelectorAll('.mode-btn');
        modeButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-mode') as BlindMode;
                this.setMode(mode);
            });
        });

        // Cancel button
        const cancelBtn = this.modal.querySelector('#cancel-btn');
        cancelBtn?.addEventListener('click', () => this.close());

        // Confirm button
        const confirmBtn = this.modal.querySelector('#confirm-btn');
        confirmBtn?.addEventListener('click', () => this.confirm());

        // Close on overlay click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.close();
            }
        });
    }

    private setMode(mode: BlindMode): void {
        this.currentMode = mode;
        this.config.mode = mode;

        // Update button styles
        const modeButtons = this.modal?.querySelectorAll('.mode-btn');
        modeButtons?.forEach(btn => {
            if (btn.getAttribute('data-mode') === mode) {
                (btn as HTMLElement).style.background = '#4CAF50';
                (btn as HTMLElement).style.borderColor = '#FFD700';
                btn.classList.add('mode-btn-active');
            } else {
                (btn as HTMLElement).style.background = '#333';
                (btn as HTMLElement).style.borderColor = '#666';
                btn.classList.remove('mode-btn-active');
            }
        });

        // Update description
        const descriptions = {
            default: '<strong>Default Mode:</strong> Blinds are calculated based on your starting balance. Blinds double when players are eliminated.',
            fixed: '<strong>Fixed Mode:</strong> Set your own initial blinds. Blinds still double when players are eliminated.',
            custom: '<strong>Custom Mode:</strong> Full control over blinds, increase frequency, and multiplier. Configure everything!'
        };

        const descText = this.modal?.querySelector('#description-text');
        if (descText) {
            descText.innerHTML = descriptions[mode];
        }

        // Update config panel
        this.updateConfigPanel();
    }

    private updateConfigPanel(): void {
        const panel = this.modal?.querySelector('#config-panel');
        if (!panel) return;

        if (this.currentMode === 'default') {
            panel.innerHTML = `
                <div style="background: rgba(0, 0, 0, 0.3); padding: 15px; border-radius: 5px; border: 1px solid #666;">
                    <p style="color: #FFDD00; margin: 0; font-size: 14px;">
                        No configuration needed. Blinds will be automatically calculated based on your starting balance.
                    </p>
                </div>
            `;
        } else if (this.currentMode === 'fixed') {
            panel.innerHTML = `
                <div style="background: rgba(0, 0, 0, 0.3); padding: 15px; border-radius: 5px; border: 1px solid #666;">
                    <div style="margin-bottom: 15px;">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Small Blind ($):
                        </label>
                        <input type="number" id="small-blind" value="${this.config.initialSmallBlind}" min="1" max="1000"
                            style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                    </div>
                    <div>
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Big Blind ($):
                        </label>
                        <input type="number" id="big-blind" value="${this.config.initialBigBlind}" min="2" max="2000"
                            style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                    </div>
                    <p style="color: #888; margin: 10px 0 0 0; font-size: 12px; font-style: italic;">
                        Note: Blinds will still double when players are eliminated.
                    </p>
                </div>
            `;

            // Add event listeners to inputs
            const sbInput = this.modal?.querySelector('#small-blind') as HTMLInputElement;
            const bbInput = this.modal?.querySelector('#big-blind') as HTMLInputElement;

            sbInput?.addEventListener('input', () => {
                this.config.initialSmallBlind = parseInt(sbInput.value) || 10;
            });

            bbInput?.addEventListener('input', () => {
                this.config.initialBigBlind = parseInt(bbInput.value) || 20;
            });
        } else if (this.currentMode === 'custom') {
            panel.innerHTML = `
                <div style="background: rgba(0, 0, 0, 0.3); padding: 15px; border-radius: 5px; border: 1px solid #666;">
                    <div style="margin-bottom: 15px;">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Small Blind ($):
                        </label>
                        <input type="number" id="small-blind" value="${this.config.initialSmallBlind}" min="1" max="1000"
                            style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                    </div>
                    <div style="margin-bottom: 15px;">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Big Blind ($):
                        </label>
                        <input type="number" id="big-blind" value="${this.config.initialBigBlind}" min="2" max="2000"
                            style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                    </div>

                    <div style="margin-bottom: 15px;">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Blind Increase Frequency:
                        </label>
                        <select id="increase-frequency" style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                            <option value="elimination" ${this.config.increaseFrequency === 'elimination' ? 'selected' : ''}>When Players Eliminated</option>
                            <option value="hands" ${this.config.increaseFrequency === 'hands' ? 'selected' : ''}>Every X Hands</option>
                            <option value="none" ${this.config.increaseFrequency === 'none' ? 'selected' : ''}>Never Increase</option>
                        </select>
                    </div>

                    <div id="hands-input-container" style="margin-bottom: 15px; ${this.config.increaseFrequency === 'hands' ? '' : 'display: none;'}">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Hands Between Increases:
                        </label>
                        <input type="number" id="hands-per-increase" value="${this.config.handsPerIncrease || 10}" min="1" max="100"
                            style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                    </div>

                    <div id="multiplier-container" style="margin-bottom: 15px; ${this.config.increaseFrequency === 'none' ? 'display: none;' : ''}">
                        <label style="color: #FFDD00; font-size: 14px; display: block; margin-bottom: 5px;">
                            Increase Multiplier:
                        </label>
                        <select id="increase-multiplier" style="width: 100%; padding: 10px; font-size: 16px; border: 2px solid #FFD700; border-radius: 5px; background: #1a0033; color: #FFDD00;">
                            <option value="1.5" ${this.config.increaseMultiplier === 1.5 ? 'selected' : ''}>×1.5 (50% increase)</option>
                            <option value="2" ${this.config.increaseMultiplier === 2 ? 'selected' : ''}>×2 (Double)</option>
                            <option value="3" ${this.config.increaseMultiplier === 3 ? 'selected' : ''}>×3 (Triple)</option>
                            <option value="5" ${this.config.increaseMultiplier === 5 ? 'selected' : ''}>×5 (5x increase)</option>
                        </select>
                    </div>
                </div>
            `;

            // Add event listeners
            const sbInput = this.modal?.querySelector('#small-blind') as HTMLInputElement;
            const bbInput = this.modal?.querySelector('#big-blind') as HTMLInputElement;
            const freqSelect = this.modal?.querySelector('#increase-frequency') as HTMLSelectElement;
            const handsInput = this.modal?.querySelector('#hands-per-increase') as HTMLInputElement;
            const multSelect = this.modal?.querySelector('#increase-multiplier') as HTMLSelectElement;
            const handsContainer = this.modal?.querySelector('#hands-input-container') as HTMLDivElement;
            const multContainer = this.modal?.querySelector('#multiplier-container') as HTMLDivElement;

            sbInput?.addEventListener('input', () => {
                this.config.initialSmallBlind = parseInt(sbInput.value) || 10;
            });

            bbInput?.addEventListener('input', () => {
                this.config.initialBigBlind = parseInt(bbInput.value) || 20;
            });

            freqSelect?.addEventListener('change', () => {
                this.config.increaseFrequency = freqSelect.value as BlindIncreaseFrequency;

                // Show/hide hands input
                if (handsContainer) {
                    handsContainer.style.display = this.config.increaseFrequency === 'hands' ? 'block' : 'none';
                }

                // Show/hide multiplier
                if (multContainer) {
                    multContainer.style.display = this.config.increaseFrequency === 'none' ? 'none' : 'block';
                }
            });

            handsInput?.addEventListener('input', () => {
                this.config.handsPerIncrease = parseInt(handsInput.value) || 10;
            });

            multSelect?.addEventListener('change', () => {
                this.config.increaseMultiplier = parseFloat(multSelect.value) || 2;
            });
        }
    }

    private confirm(): void {
        // Validate configuration
        if (this.config.initialSmallBlind <= 0 || this.config.initialBigBlind <= 0) {
            alert('Blinds must be greater than 0');
            return;
        }

        if (this.config.initialBigBlind <= this.config.initialSmallBlind) {
            alert('Big blind must be greater than small blind');
            return;
        }

        if (this.config.increaseFrequency === 'hands' && (!this.config.handsPerIncrease || this.config.handsPerIncrease <= 0)) {
            alert('Hands per increase must be greater than 0');
            return;
        }

        // Call the callback
        if (this.onConfirm) {
            this.onConfirm(this.config);
        }

        this.close();
    }

    private close(): void {
        if (this.modal) {
            this.modal.remove();
            this.modal = null;
        }
    }
}
